import { CreateMyTextPresetArgs } from '@sparemin/api-sdk';
import { Button, Preset } from '@sparemin/blockhead';
import cn from 'classnames';
import React, { useCallback } from 'react';

import { useSelector } from 'react-redux';
import FadingScrollBars from 'components/FadingScrollBars';

import { ProBadge } from 'components/icons';
import { isProSelector } from 'redux/modules/pricing';
import { ImmutableTextPreset } from 'redux/modules/text-presets';
import TextPresetButton from './TextPresetButton';

import useTextPresets from './useTextPresets';
import { block } from './utils';

export interface TextPresetSelectorProps {
  localPresets?: React.ReactNode;
  mode?: 'horizontal' | 'vertical';
  currentPresetConfig?: CreateMyTextPresetArgs;
  selectedPreset?: ImmutableTextPreset;
  onPickPreset: (presetStyles: ImmutableTextPreset) => void;
}

const TextPresetSelector: React.FunctionComponent<TextPresetSelectorProps> = props => {
  const isPro = useSelector(isProSelector);

  const {
    localPresets = [],
    mode = 'vertical',
    currentPresetConfig,
    selectedPreset,
    onPickPreset,
  } = props;

  const {
    presetIds,
    isLoading,
    onSaveTextPreset,
    onDeleteTextPreset,
  } = useTextPresets();

  const handleSaveTextPreset = useCallback((): void => {
    onSaveTextPreset(currentPresetConfig);
  }, [currentPresetConfig, onSaveTextPreset]);

  return (
    <div className={block('container')}>
      <div className={block('header')}>
        <div className={block('header-item')}>
          <Preset className={block('header-icon')} />

          <h3 className={block('title')}>Presets</h3>
        </div>

        <Button
          size="small"
          variant="ghost"
          isDisabled={isLoading}
          className={block('save-preset-button')}
          onPress={handleSaveTextPreset}
        >
          <span>save current style</span>
          {!isPro && <ProBadge />}
        </Button>
      </div>
      <FadingScrollBars
        className={cn(block('fading-scroller', { [mode]: true }))}
        horizontal={mode === 'horizontal'}
        hideTracksWhenNotNeeded
        sideFaderClassName={cn(block('fader', { [mode]: true }))}
      >
        <div className={block('body', { [mode]: true, disabled: isLoading })}>
          {localPresets}
          {presetIds.map(presetId => (
            <TextPresetButton
              key={presetId}
              isSelected={selectedPreset?.get('textPresetId') === presetId}
              mode={mode}
              onClick={onPickPreset}
              onDelete={onDeleteTextPreset}
              presetId={presetId}
            />
          ))}
        </div>
      </FadingScrollBars>
    </div>
  );
};

export default TextPresetSelector;
